import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getScheduledSessions, createScheduledSession, updateScheduledSession,
    deleteScheduledSession, getClients, getGroups
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useNewSessionModal } from '../context/NewSessionModalContext';
import LoadingLeaf from '../components/LoadingLeaf';

export default function Schedule() {
    const navigate = useNavigate();
    const { openNewSession } = useNewSessionModal();
    const [sessions, setSessions] = useState([]);
    const [clients, setClients] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [sessionMode, setSessionMode] = useState('individual');
    const { user, profile } = useAuth();
    const [form, setForm] = useState({
        client_id: '',
        client_ids: [],
        group_id: '',
        scheduled_date: new Date().toISOString().split('T')[0],
        scheduled_time: '10:00',
        duration_minutes: 60,
        title: '',
        notes: '',
    });

    useEffect(() => {
        if (user) {
            fetchClients();
            fetchGroups();
            fetchSessions();
        }
    }, [user]);

    // Split from a single Promise.all — a hiccup in scheduled sessions or groups
    // must not blank the client list (same fix as Clients.js/Dashboard.js).
    const fetchClients = async () => {
        try {
            const clientsRes = await getClients();
            setClients(clientsRes.data || []);
        } catch (err) {
            setError('Could not load clients.');
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const groupsRes = await getGroups();
            setGroups(groupsRes.data || []);
        } catch (err) {
            // non-critical — group option just won't show this load
        }
    };

    const fetchSessions = async () => {
        try {
            const sessionsRes = await getScheduledSessions();
            setSessions(sessionsRes.data || []);
        } catch (err) {
            // non-critical — scheduled sessions list just won't show this load
        }
    };

    const openNewModal = (date = null) => {
        setEditingSession(null);
        setSessionMode('individual');
        setForm({
            client_id: clients[0]?.id || '',
            client_ids: [],
            group_id: groups[0]?.id || '',
            scheduled_date: date || new Date().toISOString().split('T')[0],
            scheduled_time: '10:00',
            duration_minutes: 60,
            title: '',
            notes: '',
        });
        setShowModal(true);
    };

    const openEditModal = (session) => {
        setEditingSession(session);
        const mode = session.session_mode || 'individual';
        setSessionMode(mode);
        setForm({
            client_id: session.client_id || '',
            client_ids: session.all_clients?.map(c => c.id) || (session.client_id ? [session.client_id] : []),
            group_id: session.group_id || '',
            scheduled_date: session.scheduled_date,
            scheduled_time: session.scheduled_time || '10:00',
            duration_minutes: session.duration_minutes || 60,
            title: session.title || '',
            notes: session.notes || '',
        });
        setShowModal(true);
    };

    const toggleClientId = (clientId) => {
        setForm(prev => ({
            ...prev,
            client_ids: prev.client_ids.includes(clientId)
                ? prev.client_ids.filter(id => id !== clientId)
                : [...prev.client_ids, clientId]
        }));
    };

    const getSessionLabel = () => {
        if (sessionMode === 'group') {
            const group = groups.find(g => g.id === form.group_id);
            return group?.name || 'Group session';
        }
        if (sessionMode === 'multi') {
            const names = form.client_ids.map(id => clients.find(c => c.id === id)?.name).filter(Boolean);
            return names.length > 0 ? names.join(', ') : 'Multi-client session';
        }
        const client = clients.find(c => c.id === form.client_id);
        return client?.name || '...';
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            let data = {
                scheduled_date: form.scheduled_date,
                scheduled_time: form.scheduled_time,
                duration_minutes: form.duration_minutes,
                title: form.title || `Session with ${getSessionLabel()}`,
                notes: form.notes,
                session_mode: sessionMode,
            };

            if (sessionMode === 'individual') {
                data.client_id = form.client_id;
                data.client_ids = [form.client_id];
            } else if (sessionMode === 'multi') {
                data.client_ids = form.client_ids;
                data.client_id = form.client_ids[0];
            } else if (sessionMode === 'group') {
                data.group_id = form.group_id;
                // Get clients from group
                const group = groups.find(g => g.id === form.group_id);
                data.client_ids = form.client_ids; // optionally override
            }

            if (editingSession) {
                await updateScheduledSession(editingSession.id, data);
            } else {
                await createScheduledSession(data);
            }

            await fetchSessions();
            setShowModal(false);
        } catch (err) {
            setError('Could not save session.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Remove this scheduled session?')) return;
        try {
            await deleteScheduledSession(id);
            setSessions(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            setError('Could not delete session.');
        }
    };

    const handleStart = async (session) => {
        try {
            await updateScheduledSession(session.id, { status: 'completed' });
            setSessions(prev => prev.filter(s => s.id !== session.id));
            const clientIds = session.all_clients?.length > 0
                ? session.all_clients.map(c => c.id)
                : [session.client_id];
            openNewSession({ clientIds });
        } catch (err) {
            setError('Could not update session.');
        }
    };

    const addToGoogleCalendar = (session) => {
        const title = encodeURIComponent(session.title || 'Coaching Session');
        const date = session.scheduled_date.replace(/-/g, '');
        const time = (session.scheduled_time || '10:00').replace(':', '') + '00';
        const duration = session.duration_minutes || 60;
        const endTime = (() => {
            const [h, m] = (session.scheduled_time || '10:00').split(':').map(Number);
            const end = h * 60 + m + duration;
            return `${Math.floor(end / 60).toString().padStart(2, '0')}${(end % 60).toString().padStart(2, '0')}00`;
        })();
        const details = encodeURIComponent(session.notes || '');
        const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${date}T${time}/${date}T${endTime}&details=${details}`;
        window.open(url, '_blank');
    };

    const downloadICS = (session) => {
        const title = session.title || 'Coaching Session';
        const date = session.scheduled_date.replace(/-/g, '');
        const time = (session.scheduled_time || '10:00').replace(':', '') + '00';
        const duration = session.duration_minutes || 60;
        const endTime = (() => {
            const [h, m] = (session.scheduled_time || '10:00').split(':').map(Number);
            const end = h * 60 + m + duration;
            return `${Math.floor(end / 60).toString().padStart(2, '0')}${(end % 60).toString().padStart(2, '0')}00`;
        })();
        const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Transformation Companion//EN\nBEGIN:VEVENT\nDTSTART:${date}T${time}\nDTEND:${date}T${endTime}\nSUMMARY:${title}\nDESCRIPTION:${session.notes || ''}\nEND:VEVENT\nEND:VCALENDAR`;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
        a.download = `${title.replace(/\s+/g, '_')}.ics`;
        a.click();
    };

    const getClientNames = (session) => {
        if (session.all_clients?.length > 0) {
            return session.all_clients.map(c => c.name).join(', ');
        }
        if (session.clients?.name) return session.clients.name;
        return 'Unknown client';
    };

    const getSessionModeLabel = (session) => {
        if (session.session_mode === 'group') return `Group · ${session.groups?.name || ''}`;
        if (session.session_mode === 'multi') return `${session.all_clients?.length || 0} clients`;
        return session.clients?.session_type || 'Individual';
    };

    // Group by month
    const grouped = sessions.reduce((acc, s) => {
        const month = new Date(s.scheduled_date + 'T12:00:00').toLocaleDateString('default', { month: 'long', year: 'numeric' });
        if (!acc[month]) acc[month] = [];
        acc[month].push(s);
        return acc;
    }, {});

    const today = new Date().toISOString().split('T')[0];
    const isToday = (d) => d === today;
    const isPast = (d) => d < today;

    const formatTime = (t) => t ? new Date(`2000-01-01T${t}`).toLocaleTimeString('default', { hour: 'numeric', minute: '2-digit' }) : '';

    const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <div>
                    <div style={styles.pageTitle}>Schedule</div>
                    <div style={styles.pageSubtitle}>{sessions.length} upcoming session{sessions.length !== 1 ? 's' : ''}</div>
                </div>
                <button style={styles.addBtn} onClick={() => openNewModal()}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" /></svg>
                    Schedule session
                </button>
            </div>

            {error && <div style={styles.errorAlert}>{error}</div>}

            {loading ? (
                <div style={styles.loadingState}><LoadingLeaf size={48} label="Loading schedule…" /></div>
            ) : sessions.length === 0 ? (
                <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>📅</div>
                    <h3 style={styles.emptyTitle}>No upcoming sessions</h3>
                    <p style={styles.emptyText}>Schedule your next session with a client or group.</p>
                    <button style={styles.emptyBtn} onClick={() => openNewModal()}>Schedule a session</button>
                </div>
            ) : (
                <div style={styles.sessionList}>
                    {Object.entries(grouped).map(([month, monthSessions]) => (
                        <div key={month}>
                            <div style={styles.monthLabel}>{month}</div>
                            {monthSessions.map(session => {
                                const today_ = isToday(session.scheduled_date);
                                const past = isPast(session.scheduled_date);
                                const clientNames = getClientNames(session);
                                const modeLabel = getSessionModeLabel(session);
                                const allClients = session.all_clients || (session.clients ? [session.clients] : []);

                                return (
                                    <div key={session.id} style={{ ...styles.sessionCard, ...(today_ ? styles.sessionCardToday : {}), ...(past ? styles.sessionCardPast : {}) }}>
                                        <div style={styles.dateBlock}>
                                            <div style={{ ...styles.dayLabel, ...(today_ ? { color: 'var(--sage)', fontSize: '10px' } : {}) }}>
                                                {today_ ? 'Today' : new Date(session.scheduled_date + 'T12:00:00').toLocaleDateString('default', { weekday: 'short' })}
                                            </div>
                                            <div style={styles.dateNum}>{new Date(session.scheduled_date + 'T12:00:00').getDate()}</div>
                                            <div style={styles.dateMonth}>{new Date(session.scheduled_date + 'T12:00:00').toLocaleString('default', { month: 'short' })}</div>
                                            {session.scheduled_time && <div style={styles.timeLabel}>{formatTime(session.scheduled_time)}</div>}
                                        </div>

                                        <div style={styles.clientBlock}>
                                            {/* Avatar stack for multiple clients */}
                                            <div style={styles.avatarStack}>
                                                {allClients.slice(0, 3).map((c, i) => (
                                                    <div key={c.id} style={{ ...styles.clientInitials, marginLeft: i > 0 ? '-8px' : 0, zIndex: 3 - i, border: '2px solid white' }}>
                                                        {getInitials(c.name)}
                                                    </div>
                                                ))}
                                                {allClients.length > 3 && (
                                                    <div style={{ ...styles.clientInitials, marginLeft: '-8px', background: 'var(--border)', color: 'var(--text-soft)', fontSize: '10px' }}>
                                                        +{allClients.length - 3}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={styles.sessionTitle}>{session.title || `Session with ${clientNames}`}</div>
                                                <div style={styles.clientName}>
                                                    {clientNames}
                                                    {session.groups?.name && ` · ${session.groups.name}`}
                                                </div>
                                                <div style={styles.modeTag}>{modeLabel} · {session.duration_minutes || 60} min</div>
                                                {session.notes && <div style={styles.sessionNotes}>{session.notes}</div>}
                                            </div>
                                        </div>

                                        <div style={styles.sessionActions}>
                                            {today_ && (
                                                <button style={styles.startBtn} onClick={() => handleStart(session)}>Start →</button>
                                            )}
                                            <button style={styles.calendarBtn} onClick={() => addToGoogleCalendar(session)} title="Add to Google Calendar">
                                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="3" width="14" height="11" rx="2" /><path d="M5 1v4M11 1v4M1 7h14" /></svg>
                                                Google
                                            </button>
                                            <button style={styles.calendarBtn} onClick={() => downloadICS(session)} title="Download .ics">
                                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2v8M5 7l3 3 3-3" /><path d="M2 13h12" /></svg>
                                                .ics
                                            </button>
                                            <button style={styles.editBtn} onClick={() => openEditModal(session)}>Edit</button>
                                            <button style={styles.deleteBtn} onClick={() => handleDelete(session.id)}>×</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div style={styles.modalTitle}>{editingSession ? 'Edit session' : 'Schedule a session'}</div>
                            <button style={styles.modalClose} onClick={() => setShowModal(false)}>×</button>
                        </div>

                        {/* Mode selector */}
                        <div style={styles.modeSelector}>
                            {[
                                { id: 'individual', label: '👤 Individual' },
                                { id: 'multi', label: '👥 Multi-client' },
                                ...(profile?.session_focus !== 'individual' ? [{ id: 'group', label: '◈ Group' }] : []),
                            ].map(mode => (
                                <button
                                    key={mode.id}
                                    style={{ ...styles.modeBtn, ...(sessionMode === mode.id ? styles.modeBtnActive : {}) }}
                                    onClick={() => setSessionMode(mode.id)}
                                >
                                    {mode.label}
                                </button>
                            ))}
                        </div>

                        {/* Individual — single client picker */}
                        {sessionMode === 'individual' && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Client</label>
                                <select
                                    style={styles.input}
                                    value={form.client_id}
                                    onChange={e => setForm({ ...form, client_id: e.target.value })}
                                >
                                    <option value="">Select a client...</option>
                                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Multi-client — checkbox picker */}
                        {sessionMode === 'multi' && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Select clients</label>
                                <div style={styles.clientCheckGrid}>
                                    {clients.map(c => {
                                        const selected = form.client_ids.includes(c.id);
                                        return (
                                            <div
                                                key={c.id}
                                                style={{ ...styles.clientCheckItem, ...(selected ? styles.clientCheckItemActive : {}) }}
                                                onClick={() => toggleClientId(c.id)}
                                            >
                                                <div style={{ ...styles.clientCheckAvatar, background: selected ? 'var(--sage)' : 'var(--mint)', color: selected ? 'white' : 'var(--sage)' }}>
                                                    {getInitials(c.name)}
                                                </div>
                                                <span style={styles.clientCheckName}>{c.name}</span>
                                                {selected && <span style={styles.clientCheckMark}>✓</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                                {form.client_ids.length > 0 && (
                                    <div style={styles.selectedCount}>{form.client_ids.length} client{form.client_ids.length !== 1 ? 's' : ''} selected</div>
                                )}
                            </div>
                        )}

                        {/* Group picker */}
                        {sessionMode === 'group' && (
                            <div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Group</label>
                                    {groups.length === 0 ? (
                                        <div style={styles.noGroupsAlert}>
                                            No groups yet. <button style={styles.linkBtn} onClick={() => { setShowModal(false); navigate('/groups?new=1'); }}>Create a group →</button>
                                        </div>
                                    ) : (
                                        <select
                                            style={styles.input}
                                            value={form.group_id}
                                            onChange={e => setForm({ ...form, group_id: e.target.value })}
                                        >
                                            <option value="">Select a group...</option>
                                            {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.client_count || 0} clients)</option>)}
                                        </select>
                                    )}
                                </div>
                                {form.client_ids.length > 0 && (
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Override attendees (optional)</label>
                                        <div style={styles.clientCheckGrid}>
                                            {clients.map(c => {
                                                const selected = form.client_ids.includes(c.id);
                                                return (
                                                    <div
                                                        key={c.id}
                                                        style={{ ...styles.clientCheckItem, ...(selected ? styles.clientCheckItemActive : {}) }}
                                                        onClick={() => toggleClientId(c.id)}
                                                    >
                                                        <div style={{ ...styles.clientCheckAvatar, background: selected ? 'var(--sage)' : 'var(--mint)', color: selected ? 'white' : 'var(--sage)' }}>
                                                            {getInitials(c.name)}
                                                        </div>
                                                        <span style={styles.clientCheckName}>{c.name}</span>
                                                        {selected && <span style={styles.clientCheckMark}>✓</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={styles.formRow}>
                            <div style={{ ...styles.formGroup, flex: 1 }}>
                                <label style={styles.label}>Date</label>
                                <input type="date" style={styles.input} value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} />
                            </div>
                            <div style={{ ...styles.formGroup, flex: 1 }}>
                                <label style={styles.label}>Time</label>
                                <input type="time" style={styles.input} value={form.scheduled_time} onChange={e => setForm({ ...form, scheduled_time: e.target.value })} />
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Duration</label>
                            <select style={styles.input} value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: Number(e.target.value) })}>
                                <option value={30}>30 minutes</option>
                                <option value={45}>45 minutes</option>
                                <option value={60}>60 minutes</option>
                                <option value={75}>75 minutes</option>
                                <option value={90}>90 minutes</option>
                                <option value={120}>2 hours</option>
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Session title (optional)</label>
                            <input
                                style={styles.input}
                                placeholder={`Session with ${getSessionLabel()}`}
                                value={form.title}
                                onChange={e => setForm({ ...form, title: e.target.value })}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Notes (optional)</label>
                            <textarea
                                style={styles.textarea}
                                placeholder="Agenda, preparation notes, what to focus on..."
                                value={form.notes}
                                onChange={e => setForm({ ...form, notes: e.target.value })}
                            />
                        </div>

                        <div style={styles.modalFooter}>
                            <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
                            <button
                                style={{
                                    ...styles.saveBtn,
                                    opacity: (sessionMode === 'individual' && !form.client_id) ||
                                        (sessionMode === 'multi' && form.client_ids.length === 0) ||
                                        (sessionMode === 'group' && !form.group_id) ? 0.5 : 1
                                }}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : editingSession ? 'Save changes' : 'Schedule session'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif", maxWidth: '860px' },
    header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '32px' },
    pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
    pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
    addBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 20px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px' },
    loadingState: { padding: '60px', textAlign: 'center', color: 'var(--text-soft)', fontSize: '14px' },
    emptyState: { textAlign: 'center', padding: '80px 24px' },
    emptyIcon: { fontSize: '40px', marginBottom: '16px' },
    emptyTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '8px' },
    emptyText: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '20px' },
    emptyBtn: { padding: '11px 22px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', cursor: 'pointer' },
    sessionList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    monthLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-faint)', marginBottom: '12px', marginTop: '24px' },
    sessionCard: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '18px 22px', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: 'var(--shadow-sm)' },
    sessionCardToday: { borderColor: 'var(--sage)', borderWidth: '2px' },
    sessionCardPast: { opacity: 0.55 },
    dateBlock: { minWidth: '64px', textAlign: 'center', flexShrink: 0 },
    dayLabel: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', marginBottom: '2px' },
    dateNum: { fontFamily: "'Lora', Georgia, serif", fontSize: '26px', color: 'var(--text-dark)', lineHeight: 1 },
    dateMonth: { fontSize: '11px', color: 'var(--text-soft)', marginTop: '2px' },
    timeLabel: { fontSize: '11px', color: 'var(--sage)', marginTop: '4px', fontWeight: '500' },
    clientBlock: { display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 },
    avatarStack: { display: 'flex', flexShrink: 0 },
    clientInitials: { width: '36px', height: '36px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0 },
    sessionTitle: { fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '2px' },
    clientName: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '2px' },
    modeTag: { fontSize: '11px', color: 'var(--text-faint)' },
    sessionNotes: { fontSize: '12px', color: 'var(--text-faint)', marginTop: '4px', fontStyle: 'italic' },
    sessionActions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' },
    startBtn: { padding: '8px 14px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' },
    calendarBtn: { display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 11px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    editBtn: { padding: '7px 12px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer' },
    deleteBtn: { background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: '20px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(26,46,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(2px)' },
    modal: { background: 'white', borderRadius: '16px', width: '520px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' },
    modalHeader: { padding: '24px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
    modalTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)' },
    modalClose: { background: 'none', border: 'none', fontSize: '24px', color: 'var(--text-faint)', cursor: 'pointer' },
    modeSelector: { display: 'flex', gap: '8px', padding: '0 28px', marginBottom: '20px' },
    modeBtn: { flex: 1, padding: '9px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', color: 'var(--text-soft)' },
    modeBtnActive: { background: 'var(--mint)', borderColor: 'var(--sage)', color: 'var(--sage)', fontWeight: '600' },
    formGroup: { marginBottom: '18px', padding: '0 28px' },
    formRow: { display: 'flex', gap: '16px', padding: '0 28px', marginBottom: '18px' },
    label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '8px' },
    input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', minHeight: '80px', resize: 'vertical' },
    clientCheckGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    clientCheckItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--cream)' },
    clientCheckItemActive: { border: '1px solid var(--sage)', background: 'var(--mint)' },
    clientCheckAvatar: { width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', flexShrink: 0 },
    clientCheckName: { fontSize: '13px', color: 'var(--text-dark)', fontWeight: '500' },
    clientCheckMark: { fontSize: '11px', color: 'var(--sage)', fontWeight: '700' },
    selectedCount: { fontSize: '12px', color: 'var(--sage)', marginTop: '8px', fontWeight: '500' },
    noGroupsAlert: { background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px', fontSize: '13.5px', color: 'var(--text-soft)' },
    linkBtn: { background: 'none', border: 'none', color: 'var(--sage)', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '500', padding: 0 },
    modalFooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '16px 28px 24px', borderTop: '1px solid var(--border-light)', marginTop: '8px' },
    cancelBtn: { padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
    saveBtn: { padding: '10px 20px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500' },
};